import React, { useState, useEffect, createContext, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  BiSearch, 
  BiPlus, 
  BiGridAlt, 
  BiListUl,
  BiHomeAlt,
  BiStore,
  BiBook,
  BiCog,
  BiGroup,
  BiChart,
  BiDollar,
  BiLinkAlt,
  BiLayer,
  BiFolder,
  BiBookContent,
  BiBell,
  BiLogOut,
  BiUser,
  BiChevronDown,
  BiTrash,
  BiEdit,
  BiLink,
  BiMoon,
  BiSun,
  BiFont,
  BiEnvelope,
  BiX,
  BiNetworkChart
} from 'react-icons/bi'
import FormEmails from './components/Dashboard/FormEmails'
import Orders from './components/Dashboard/Orders'
import CustomFonts from './components/Dashboard/CustomFonts'
import { buildApiUrl } from './config/api'
import './Dashboard.css'

// Create theme context
const ThemeContext = createContext();

function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [newProjectName, setNewProjectName] = useState('');
  const [viewType, setViewType] = useState('grid');
  const [workspaceFilter, setWorkspaceFilter] = useState('Command Center');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('Date created');
  const [activeTab, setActiveTab] = useState('projects');
  const [userData, setUserData] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [theme, setTheme] = useState('dark');
  const [newProject, setNewProject] = useState({
    name: '',
    description: ''
  });
  const [apiError, setApiError] = useState(null);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [formEmail, setFormEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    console.log(projects);
  }, [projects]);
  
  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
  };

  // Load saved theme on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'dark';
    setTheme(savedTheme);
  }, []);
  
  // Fetch user data and projects on component mount
  useEffect(() => {
    const checkAuthAndFetchData = async () => {
      setLoading(true);
      
      try {
        // Simulate network delay (remove this in production)
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const user = localStorage.getItem('user');
        
        if (user) {
          const userData = JSON.parse(user);
          setUserData(userData);
          
          // Now fetch the user's projects
          fetchUserProjects(userData.id);
        } else {
          // Redirect to sign in if no user is found
          navigate('/signin');
        }
      } catch (error) {
        console.error('Authentication error:', error);
        setApiError("Failed to authenticate user");
        navigate('/signin');
      }
    };
    
    checkAuthAndFetchData();
  }, [navigate]);
  
  // Function to fetch user projects
  const fetchUserProjects = async (userId) => {
    setPageLoading(true);
    
    try {
      const response = await fetch(buildApiUrl(`api/sites/user/${userId}`));
      const data = await response.json();
      
      if (response.ok) {
        // Fetch form emails and deployment info for each project
        const projectsWithDetails = await Promise.all(
          (data.sites || []).map(async (project) => {
            try {
              // Fetch form email
              const emailResponse = await fetch(buildApiUrl(`api/sites/${project.siteId}/form-email`));
              const emailData = await emailResponse.json();
              
              // Fetch deployment information
              const deploymentResponse = await fetch(buildApiUrl(`api/sites/${project.siteId}/deployments`));
              const deploymentData = await deploymentResponse.json();
              
              const projectWithDetails = {
                ...project,
                formEmail: emailResponse.ok ? emailData.formEmail : null,
                deployments: deploymentResponse.ok ? deploymentData.deployments : null,
                hasCustomDomains: deploymentResponse.ok ? deploymentData.summary?.hasAnyCustomDomain : false,
                customDomains: deploymentResponse.ok ? deploymentData.summary?.customDomains : []
              };
              
              return projectWithDetails;
            } catch (error) {
              console.error(`Error fetching details for project ${project.siteId}:`, error);
              return {
                ...project,
                formEmail: null,
                deployments: null,
                hasCustomDomains: false,
                customDomains: []
              };
            }
          })
        );
        
        setProjects(projectsWithDetails || []);
        setApiError(null);
      } else {
        console.error('Error fetching projects:', data.error);
        setApiError(data.error || "Failed to fetch projects");
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      setApiError("Network error: Cannot connect to server");
    } finally {
      setLoading(false);
      setPageLoading(false);
    }
  };
  
  // Function to create a new project
  const handleCreateProject = async (e) => {
    e.preventDefault();
    
    if (!newProject.name.trim()) {
      setApiError("Project name is required");
      return;
    }
    
    setPageLoading(true);
    
    try {
      const response = await fetch(buildApiUrl('api/sites/create'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newProject.name,
          description: newProject.description,
          userId: userData.id
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Add the new project to the list
        setProjects([data.site, ...projects]);
        
        // Close the modal and reset the form
        setIsCreateModalOpen(false);
        setNewProject({ name: '', description: '' });
        setApiError(null);
      } else {
        console.error('Error creating project:', data.error);
        setApiError(data.error || "Failed to create project");
      }
    } catch (error) {
      console.error('Error creating project:', error);
      setApiError("Network error: Cannot connect to server");
    } finally {
      setPageLoading(false);
    }
  };
  
  // Function to delete a project
  const handleDeleteProject = async (siteId) => {
    if (!window.confirm("Are you sure you want to delete this project? This action cannot be undone.")) {
      return;
    }
    
    setPageLoading(true);
    
    try {
      const response = await fetch(buildApiUrl(`api/sites/${siteId}`), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: userData.id
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Remove the deleted project from the list
        setProjects(projects.filter(project => project.siteId !== siteId));
        setApiError(null);
      } else {
        console.error('Error deleting project:', data.error);
        setApiError(data.error || "Failed to delete project");
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      setApiError("Network error: Cannot connect to server");
    } finally {
      setPageLoading(false);
    }
  };
  
  // Function to open the project editor
  const handleEditProject = (siteId) => {
    // Navigate to the project editor with the site ID parameter
    navigate(`/editor/${siteId}`);
  };
  
  // Function to view the published site
  const handleViewSite = (url) => {
    window.open(url, '_blank');
  };

  const handleDNSInstructions = (siteId) => {
    navigate(`/dns-instructions/${siteId}`);
  };

  const hasCustomDomains = (project) => {
    // Check if project has custom domains configured using the fetched deployment data
    return project.hasCustomDomains || false;
  };
  
  // Function to handle input changes in the create project form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewProject({
      ...newProject,
      [name]: value
    });
  };
  
  // Function to handle form email click
  const handleFormEmailClick = (project) => {
    setCurrentProject(project);
    setFormEmail(project.formEmail || '');
    setIsEmailModalOpen(true);
  };
  
  // Function to update form email
  const handleUpdateFormEmail = async (e) => {
    e.preventDefault();
    
    if (!currentProject) return;
    
    setEmailLoading(true);
    
    try {
      const method = formEmail ? 'PUT' : 'DELETE';
      const url = buildApiUrl(`api/sites/${currentProject.siteId}/form-email`);
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          formEmail: formEmail || null,
          userId: userData.id
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Update the project in the projects list
        setProjects(projects.map(p => 
          p.siteId === currentProject.siteId 
            ? { ...p, formEmail: formEmail || null } 
            : p
        ));
        
        // Close the modal
        setIsEmailModalOpen(false);
        setApiError(null);
      } else {
        console.error('Error updating form email:', data.error);
        setApiError(data.error || "Failed to update form email");
      }
    } catch (error) {
      console.error('Error updating form email:', error);
      setApiError("Network error: Cannot connect to server");
    } finally {
      setEmailLoading(false);
    }
  };
  
  // Function to remove form email
  const handleRemoveFormEmail = async () => {
    if (!currentProject) return;
    
    if (!window.confirm("Are you sure you want to remove the form email?")) {
      return;
    }
    
    setEmailLoading(true);
    
    try {
      const response = await fetch(buildApiUrl(`api/sites/${currentProject.siteId}/form-email`), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: userData.id
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Update the project in the projects list
        setProjects(projects.map(p => 
          p.siteId === currentProject.siteId 
            ? { ...p, formEmail: null } 
            : p
        ));
        
        // Close the modal
        setIsEmailModalOpen(false);
        setApiError(null);
      } else {
        console.error('Error removing form email:', data.error);
        setApiError(data.error || "Failed to remove form email");
      }
    } catch (error) {
      console.error('Error removing form email:', error);
      setApiError("Network error: Cannot connect to server");
    } finally {
      setEmailLoading(false);
    }
  };
  
  const handleSignOut = () => {
    // Clear user data from localStorage
    localStorage.removeItem('user');
    
    // Redirect to sign in page
    navigate('/signin');
  };
  
  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };
  
  // Get user initials for the avatar
  const getUserInitials = () => {
    if (!userData) return 'U';
    
    if (userData.username) {
      return userData.username.charAt(0).toUpperCase();
    }
    
    if (userData.email) {
      return userData.email.charAt(0).toUpperCase();
    }
    
    return 'U';
  };
  
  // If still loading, show loading screen
  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-content">
          <div className="cosmic-loader">
            <div className="cosmic-loader-ring"></div>
            <div className="cosmic-loader-ring"></div>
            <div className="cosmic-loader-ring"></div>
          </div>
          <h2>Initializing Dashboard</h2>
          <p>Establishing secure connection...</p>
        </div>
      </div>
    );
  }
  
  const renderTabContent = () => {
    switch(activeTab) {
      case 'projects':
        return (
          <>
            <div className="workspace-tabs">
              <div className="workspace-tab active">My Workspace</div>
              <div className="workspace-tab starter">Settings</div>
              
              <div className="workspace-metrics">
                <div className="workspace-metric">
                  <span className="metric-label">Active Projects</span>
                  <span className="metric-value-small">{projects.length}</span>
                </div>
                <div className="workspace-metric">
                  <span className="metric-label">Storage</span>
                  <span className="metric-value-small">42%</span>
                  <div className="small-progress">
                    <div className="small-progress-bar" style={{width: '42%'}}></div>
                  </div>
                </div>
              </div>
            </div>
            
            {apiError && (
              <div className="api-error">
                <p>{apiError}</p>
                <button onClick={() => setApiError(null)}>Dismiss</button>
              </div>
            )}
            
            <div className="sites-toolbar">
              <div className="search-container">
                <BiSearch className="search-icon" />
                <input 
                  type="text" 
                  className="search-input"
                  placeholder="Scan projects..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              <div className="toolbar-actions">
                <div className="sort-dropdown">
                  <select 
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option>Date created</option>
                    <option>Alphabetical</option>
                    <option>Last updated</option>
                  </select>
                </div>
                
                <div className="view-controls">
                  <button 
                    className={`view-btn ${viewType === 'grid' ? 'active' : ''}`}
                    onClick={() => setViewType('grid')}
                  >
                    <BiGridAlt />
                  </button>
                  <button 
                    className={`view-btn ${viewType === 'list' ? 'active' : ''}`}
                    onClick={() => setViewType('list')}
                  >
                    <BiListUl />
                  </button>
                </div>
                
                <button className="new-site-btn" onClick={() => setIsCreateModalOpen(true)}>
                  <BiPlus /> New Project
                </button>
              </div>
            </div>
            
            {pageLoading ? (
              <div className="page-loading">
                <div className="spinner"></div>
                <p>Loading projects...</p>
              </div>
            ) : projects.length === 0 ? (
              <div className="no-projects">
                <div className="empty-state">
                  <div className="empty-icon">🚀</div>
                  <h3>No Projects Yet</h3>
                  <p>Create your first cosmic website project to get started.</p>
                  <button className="new-site-btn" onClick={() => setIsCreateModalOpen(true)}>
                    <BiPlus /> Create First Project
                  </button>
                </div>
              </div>
            ) : (
              <div className={`sites-grid ${viewType === 'list' ? 'list-view' : ''}`}>
                {projects
                  .filter(project => 
                    project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    (project.description && project.description.toLowerCase().includes(searchQuery.toLowerCase()))
                  )
                  .sort((a, b) => {
                    if (sortBy === 'Alphabetical') {
                      return a.name.localeCompare(b.name);
                    } else if (sortBy === 'Last updated') {
                      return new Date(b.updatedAt) - new Date(a.updatedAt);
                    } else {
                      return new Date(b.createdAt) - new Date(a.createdAt);
                    }
                  })
                  .map(project => (
                    <div key={project.siteId} className="site-card">
                      <div className="site-thumbnail">
                        <img 
                          src={`https://source.unsplash.com/random/300x200?space&sig=${project.siteId}`} 
                          alt={project.name} 
                        />
                      </div>
                      <div className="site-info">
                        <h3 className="site-name">{project.name}</h3>
                        <p className="site-url">{project.url}</p>
                        <div className="site-description">{project.description || 'No description'}</div>
                        <div className="site-date">
                          Created: {new Date(project.createdAt).toLocaleDateString()}
                        </div>
                        <div className="site-email">
                          <span>Form Email: </span>
                          {project.formEmail ? (
                            <span className="email-set">{project.formEmail}</span>
                          ) : (
                            <span className="email-not-set">Not set</span>
                          )}
                          <button 
                            className="email-action-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFormEmailClick(project);
                            }}
                          >
                            {project.formEmail ? 'Edit' : 'Set Email'}
                          </button>
                        </div>
                        {project.hasCustomDomains && (
                          <div className="custom-domains-info">
                            <span className="custom-domain-badge">
                              <BiNetworkChart /> Custom Domain
                            </span>
                            <div className="custom-domains-list">
                              {project.customDomains.map((domain, index) => (
                                <div key={index} className="custom-domain-item">
                                  <span className="domain-name">{domain.domain}</span>
                                  <span className={`domain-status ${domain.status}`}>
                                    {domain.status === 'active' ? 'Active' : 'Pending'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="site-actions">
                        <button 
                          className="site-action-btn edit"
                          onClick={() => handleEditProject(project.siteId)}
                        >
                          <BiEdit /> Edit
                        </button>
                        <button 
                          className="site-action-btn view"
                          onClick={() => handleViewSite(project.url)}
                        >
                          <BiLink /> View
                        </button>
                        {hasCustomDomains(project) && (
                          <button 
                            className="site-action-btn dns"
                            onClick={() => handleDNSInstructions(project.siteId)}
                            title="DNS Configuration"
                          >
                            <BiNetworkChart /> DNS
                          </button>
                        )}
                        <button 
                          className="site-action-btn delete"
                          onClick={() => handleDeleteProject(project.siteId)}
                        >
                          <BiTrash />
                        </button>
                      </div>
                    </div>
                  ))
                }
              </div>
            )}
            
            {/* Create Project Modal */}
            {isCreateModalOpen && (
              <div className="modal-overlay" onClick={() => setIsCreateModalOpen(false)}>
                <div className="modal" onClick={e => e.stopPropagation()}>
                  <div className="modal-header">
                    <h2>Create New Project</h2>
                    <button className="close-modal" onClick={() => setIsCreateModalOpen(false)}>&times;</button>
                  </div>
                  
                  <form onSubmit={handleCreateProject}>
                    <div className="form-group-modal">
                      <label htmlFor="project-name">Project Name</label>
                      <input
                        type="text"
                        id="project-name"
                        name="name"
                        value={newProject.name}
                        onChange={handleInputChange}
                        placeholder="Enter project name"
                        required
                      />
                    </div>
                    
                    <div className="form-group-modal">
                      <label htmlFor="project-description">Description (optional)</label>
                      <textarea
                        id="project-description"
                        name="description"
                        value={newProject.description}
                        onChange={handleInputChange}
                        placeholder="Enter project description"
                        rows="3"
                      ></textarea>
                    </div>
                    
                    <div className="modal-actions">
                      <button 
                        type="button" 
                        className="cancel-btn"
                        onClick={() => setIsCreateModalOpen(false)}
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        className="submit-btn"
                        disabled={pageLoading}
                      >
                        {pageLoading ? 'Creating...' : 'Create Project'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
            
            {/* Form Email Modal */}
            {isEmailModalOpen && currentProject && (
              <div className="modal-overlay" onClick={() => setIsEmailModalOpen(false)}>
                <div className="modal" onClick={e => e.stopPropagation()}>
                  <div className="modal-header">
                    <h2>
                      <BiEnvelope className="modal-icon" />
                      {currentProject.formEmail ? 'Update Form Email' : 'Set Form Email'}
                    </h2>
                    <button className="close-modal" onClick={() => setIsEmailModalOpen(false)}>
                      <BiX />
                    </button>
                  </div>
                  
                  <div className="modal-content">
                    <p className="site-context">Site: <strong>{currentProject.name}</strong></p>
                    
                    <form onSubmit={handleUpdateFormEmail}>
                      <div className="form-group-modal">
                        <label htmlFor="form-email">Email Address</label>
                        <input
                          type="email"
                          id="form-email"
                          value={formEmail}
                          onChange={(e) => setFormEmail(e.target.value)}
                          placeholder="Enter email address for form submissions"
                          required
                        />
                        <p className="form-hint">
                          This email will receive all form submissions from this site.
                        </p>
                      </div>
                      
                      <div className="modal-actions">
                        {currentProject.formEmail && (
                          <button 
                            type="button" 
                            className="delete-btn"
                            onClick={handleRemoveFormEmail}
                            disabled={emailLoading}
                          >
                            Remove Email
                          </button>
                        )}
                        <button 
                          type="button" 
                          className="cancel-btn"
                          onClick={() => setIsEmailModalOpen(false)}
                        >
                          Cancel
                        </button>
                        <button 
                          type="submit" 
                          className="submit-btn"
                          disabled={emailLoading}
                        >
                          {emailLoading ? 'Saving...' : 'Save Email'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}
          </>
        );
      case 'emails':
        return <FormEmails theme={theme} projects={projects} />;
      case 'orders':
        return <Orders theme={theme} projects={projects} />;
      case 'fonts':
        return <CustomFonts theme={theme} projects={projects} />;
      default:
        return null;
    }
  };

  return (
    <div className="dashboard-container" data-theme={theme}>
      <aside className="dashboard-sidebar">
        <div className="logo">TWIL</div>
        
        <div className="workspace-selector">
          <div className="workspace-icon">
            <span>{getUserInitials()}</span>
          </div>
          <div className="workspace-name">
            {userData ? userData.username || "My Workspace" : "My Workspace"}
          </div>
        </div>
        
        <nav className="sidebar-nav">
          <a href="#" className={`sidebar-item ${activeTab === 'projects' ? 'active' : ''}`} onClick={(e) => {
            e.preventDefault();
            setActiveTab('projects');
          }}>
            <BiFolder className="sidebar-icon" />
            <span>Project Archive</span>
          </a>
          <a href="#" className={`sidebar-item ${activeTab === 'fonts' ? 'active' : ''}`} onClick={(e) => {
            e.preventDefault();
            setActiveTab('fonts');
          }}>
            <BiFont className="sidebar-icon" />
            <span>Custom Fonts</span>
          </a>
         
    
        </nav>
      </aside>
      
      <main className="dashboard-main">
        <header className="dashboard-header">
          <nav className="main-nav">
            <a 
              href="#" 
              className={`nav-item ${activeTab === 'projects' ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                setActiveTab('projects');
              }}
            >
              <BiHomeAlt className="nav-icon" />
              <span>All Projects</span>
            </a>
            <a 
              href="#" 
              className={`nav-item ${activeTab === 'emails' ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                setActiveTab('emails');
              }}
            >
              <BiStore className="nav-icon" />
              <span>Form Emails</span>
            </a>
            <a 
              href="#" 
              className={`nav-item ${activeTab === 'orders' ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                setActiveTab('orders');
              }}
            >
              <BiBook className="nav-icon" />
              <span>Orders</span>
            </a>
          </nav>
          
          <div className="header-right">
            <button 
              className="theme-toggle"
              onClick={toggleTheme}
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <BiSun className="icon" /> : <BiMoon className="icon" />}
            </button>
            <div className="notifications">
              <BiBell className="icon" />
            </div>
            <div className="user-avatar">
              {getUserInitials()}
            </div>
            <div className="account-dropdown-container">
              <div 
                className="account-dropdown" 
                onClick={toggleDropdown}
              >
                {userData ? userData.username : "Commander"}
                <BiChevronDown className={`dropdown-icon ${dropdownOpen ? 'open' : ''}`} />
              </div>
              
              {dropdownOpen && (
                <div className="dropdown-menu">
                  <div className="dropdown-header">
                    <div className="dropdown-user-info">
                      <span className="user-name">{userData ? userData.username : "User"}</span>
                      <span className="user-email">{userData ? userData.email : "email@example.com"}</span>
                    </div>
                  </div>
                  <div className="dropdown-items">
                    <a href="#" className="dropdown-item">
                      <BiUser className="dropdown-item-icon" />
                      <span>Profile</span>
                    </a>
                    <a href="#" className="dropdown-item">
                      <BiCog className="dropdown-item-icon" />
                      <span>Settings</span>
                    </a>
                    <div className="dropdown-divider"></div>
                    <a 
                      href="#" 
                      className="dropdown-item signout"
                      onClick={(e) => {
                        e.preventDefault();
                        handleSignOut();
                      }}
                    >
                      <BiLogOut className="dropdown-item-icon" />
                      <span>Sign Out</span>
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>
        
        <div className="dashboard-content">
          {renderTabContent()}
        </div>
      </main>
    </div>
  )
}

export default Dashboard